/**
 * The access tiers as cards with what each one means (UI/UX fix round A, review U5), and the Archive confirmation:
 * moving a blob to Archive takes it offline (reading needs a rehydration of up to 15 hours) and has a 180-day minimum
 * — the one tier change that must not look like choosing Hot.
 */
import type { StorageAccessTier } from '../../../../../types/azureStorage';
import { RadioGroup } from '../../../../../components/ui/radio-group';
import { RadioCard } from '../../../../../components/ui/radio-card';

export const TIERS: StorageAccessTier[] = ['Hot', 'Cool', 'Cold', 'Archive'];

export const TIER_NOTES: Record<StorageAccessTier, string> = {
  Hot: 'For data you read often. The highest storage price and the lowest price to read.',
  Cool: 'For data you read rarely. Kept for at least 30 days: moving or deleting it sooner is charged for the rest of the 30 days.',
  Cold: 'For data you read very rarely. Kept for at least 90 days, charged the same way.',
  Archive: "Offline: the blob can't be read or downloaded until it is rehydrated to another tier, which can take up to 15 hours. Kept for at least 180 days.",
};

export type TierChoiceState = { tier: StorageAccessTier; blockedReason?: string; current: boolean };

/** U40 (A+B merge): the shared RadioGroup + card radios, not native radios. */
export function TierChoices({ choices, value, onChange }: { choices: TierChoiceState[]; value: StorageAccessTier | ''; onChange: (tier: StorageAccessTier) => void }) {
  return (
    <RadioGroup value={value} onValueChange={(tier) => onChange(tier as StorageAccessTier)} aria-label="Access tier">
      {choices.map(({ tier, blockedReason, current }) => (
        <RadioCard
          key={tier} value={tier} label={tier} checked={value === tier} disabled={!!blockedReason || current}
          title={blockedReason ?? (current ? 'Already this tier' : undefined)} note={current ? 'Already this tier' : undefined}
          detail={TIER_NOTES[tier]} testId={`storage-tier-choice-${tier}`}
        >
          {blockedReason && !current && <span className="ml-6 block text-xs text-muted-foreground" data-testid={`storage-tier-reason-${tier}`}>Not available: {blockedReason}</span>}
        </RadioCard>
      ))}
    </RadioGroup>
  );
}

export function ArchiveConfirm({ blobName }: { blobName: string }) {
  return (
    <div className="space-y-1 rounded-md border border-warning/40 bg-warning/10 p-3 text-sm" role="alert" data-testid="storage-tier-archive-confirm">
      <p className="font-medium text-foreground">Archive {blobName}?</p>
      <p className="text-foreground">It can't be read or downloaded until you rehydrate it to another tier, and rehydration can take up to 15 hours.</p>
      <p className="text-foreground">Moving it out of Archive or deleting it within 180 days is charged as if it had stayed the full 180 days.</p>
    </div>
  );
}
